import Raven from 'raven-js';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { EMAIL_GENERIC_CHANNEL_ID, FORMS_GENERIC_CHANNEL_ID, LIVE_CHAT_GENERIC_CHANNEL_ID, FB_MESSENGER_GENERIC_CHANNEL_ID, WHATSAPP_GENERIC_CHANNEL_ID, CALLING_GENERIC_CHANNEL_ID } from 'conversations-message-history/common-message-format/constants/genericChannelIds';
import { CAPACITY_CATEGORIES, CAPACITY_CONFIG_TYPES } from '../constants';
export const defaultLimitToInfinity = ({
  capacityLimit,
  capacityLoad
}) => ({
  capacityLimit: capacityLimit !== null && capacityLimit !== void 0 ? capacityLimit : Infinity,
  capacityLoad
});
export const isCapacityFull = capacity => {
  var _capacity$capacityLim;
  return !!capacity && capacity.capacityLoad >= ((_capacity$capacityLim = capacity.capacityLimit) !== null && _capacity$capacityLim !== void 0 ? _capacity$capacityLim : Infinity);
};
export const hasLimit = capacity => {
  return !!capacity && typeof capacity.capacityLimit === 'number';
};
export const checkIsOverCapacity = ({
  capacityLoad,
  capacityLimit
}) => capacityLimit !== undefined && capacityLoad > capacityLimit;
export const getFullCapacityMessage = (capacity, fullMessage, overMessage) => checkIsOverCapacity(capacity) ? overMessage : fullMessage;

// validate configuration type enum, default to TOTAL
export const getConfigurationType = configurationType => {
  const checkedString = configurationType && readUnhandledEnumCase(configurationType);
  switch (checkedString) {
    case CAPACITY_CONFIG_TYPES.TOTAL:
    case CAPACITY_CONFIG_TYPES.BY_CAPACITY_CATEGORY:
      return checkedString;
    default:
      return CAPACITY_CONFIG_TYPES.TOTAL;
  }
};

// validate category enums
export const getCapacityByCategory = capacityByCategory => ({
  ASYNC_COMMUNICATION: capacityByCategory[CAPACITY_CATEGORIES.ASYNC_COMMUNICATION],
  CALLING: capacityByCategory[CAPACITY_CATEGORIES.CALLING],
  REAL_TIME_MESSAGING: capacityByCategory[CAPACITY_CATEGORIES.REAL_TIME_MESSAGING]
});
export const getUserCapacityData = userCapacityData => {
  const type = getConfigurationType(userCapacityData.capacityConfigurationType);
  switch (type) {
    case 'BY_CAPACITY_CATEGORY':
      return {
        type,
        capacity: getCapacityByCategory(userCapacityData.categoryUserCapacityData)
      };
    case 'TOTAL':
      return {
        type,
        capacity: userCapacityData.totalTicketUserCapacityData
      };
    default:
      return null;
  }
};
const formatCategoryCapacity = message => {
  const limits = message.capacityLimitsByCategory || {};
  const loads = message.capacityLoadsByCategory;
  const categories = Object.values(CAPACITY_CATEGORIES);
  return categories.reduce((acc, category) => {
    acc[category] = {
      capacityLimit: limits[category],
      capacityLoad: loads[category]
    };
    return acc;
  }, {});
};

/**
 * Converts a realtime capacity update message to equivalent chirp response
 * @param message - The realtime message to convert
 * @returns The converted response or null if there is an error parsing the message.
 */
export const realtimeMessageToResponse = message => {
  try {
    var _message$capacityLimi;
    return {
      capacityConfigurationType: message.configurationType,
      categoryUserCapacityData: formatCategoryCapacity(message),
      totalTicketUserCapacityData: {
        capacityLimit: (_message$capacityLimi = message.capacityLimit) !== null && _message$capacityLimi !== void 0 ? _message$capacityLimi : undefined,
        capacityLoad: message.capacityLoad
      }
    };
  } catch (e) {
    Raven.captureException(e);
    return null;
  }
};

// TODO: refactor, add assignee capacity utils
export const isAgentOnCall = agent => {
  var _agent$userCapacityDa;
  const callLoad = (_agent$userCapacityDa = agent.userCapacityDataResult) === null || _agent$userCapacityDa === void 0 || (_agent$userCapacityDa = _agent$userCapacityDa.categoryCapacityData) === null || _agent$userCapacityDa === void 0 || (_agent$userCapacityDa = _agent$userCapacityDa.CALLING) === null || _agent$userCapacityDa === void 0 ? void 0 : _agent$userCapacityDa.capacityLoad;
  return !!callLoad && callLoad > 0;
};

/**
 * Checks if a value is nullish (null or undefined)
 * @param value - The value to check
 * @returns true if the value is null or undefined, false otherwise
 */
export const checkIsNullish = value => value === null || value === undefined;

/**
 * Maps a generic channel ID to the appropriate capacity category for routing purposes.
 * This follows the logic from the backend CapacityCategory.java fromTicketChannel method.
 *
 * @param genericChannelId - The generic channel ID to map
 * @returns The corresponding capacity category
 */
export const getCapacityCategoryFromChannelId = genericChannelId => {
  // channel-less tickets default to ASYNC_COMMUNICATION category
  if (!genericChannelId) {
    return CAPACITY_CATEGORIES.ASYNC_COMMUNICATION;
  }
  switch (genericChannelId) {
    case CALLING_GENERIC_CHANNEL_ID:
      return CAPACITY_CATEGORIES.CALLING;
    case LIVE_CHAT_GENERIC_CHANNEL_ID:
    case FB_MESSENGER_GENERIC_CHANNEL_ID:
    case WHATSAPP_GENERIC_CHANNEL_ID:
      return CAPACITY_CATEGORIES.REAL_TIME_MESSAGING;
    case EMAIL_GENERIC_CHANNEL_ID:
    case FORMS_GENERIC_CHANNEL_ID:
      return CAPACITY_CATEGORIES.ASYNC_COMMUNICATION;
    default:
      // Custom channels default to REAL_TIME_MESSAGING category
      return CAPACITY_CATEGORIES.REAL_TIME_MESSAGING;
  }
};